import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button, Dropdown, Modal, Upload, message, Select, Spin, Tooltip, DatePicker, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt, faPaperclip, faPaperPlane, faUpRightFromSquare,
  faWandMagicSparkles, faChevronDown, faChevronUp, faTimes, faTrash, faClock, faFloppyDisk,
} from '@fortawesome/free-solid-svg-icons';
import { AntdButton, InputField, SelectInput } from '../../../blueplanitUtils/InputField';
import { setLoader } from '../../../Slices/loaderSlice';
import { fetchAllEmailTemplates, saveDraftThunk, discardDraftThunk } from '../../../Slices/salesSlice.js';
import './Frames.scss';
import RichTextEditor from '../../../blueplanitUtils/RichTextEditor';
import { inlineStylesForEmail } from '../../../blueplanitUtils/emailHtmlInliner';
import { setCurrentMailId, setOpenWindow, prependToFolder } from '../../../Slices/notification.js';
import { forwardEmailThunk, replyToMailThunk, sendEmailsThunk, sendSharedMailboxEmail, sendSesEmailThunk } from '../../../Slices/Thunk/notification.js';
import { generateEmailDraftThunk, clearEmailDraft } from '../../../Slices/aiSalesSlice.js';
import { SERVER_URL } from '../../../databucket';

// Replaces {{token}} placeholders in a template subject/body with live lead data.
const applyTemplateVariables = (text, salesInfo, loginUser) => {
  if (!text) return text;
  const primary     = salesInfo?.leadsContacts?.[0] || {};
  const contactName = primary.contactPerson || '';
  const senderFirst = loginUser?.firstname || loginUser?.firstName || '';
  const senderLast  = loginUser?.lastname  || loginUser?.lastName  || '';
  const contactFirst = contactName.split(' ')[0] || '';
  return text
    .replace(/\{\{agencyName\}\}/g,       salesInfo?.agencyName || '')
    .replace(/\{\{contactName\}\}/g,      contactName)
    .replace(/\{\{contactFirstName\}\}/g, contactFirst)
    .replace(/\{\{firstName\}\}/g,        contactFirst)
    .replace(/\{\{contactTitle\}\}/g,     primary.title || '')
    .replace(/\{\{city\}\}/g,             salesInfo?.city  || '')
    .replace(/\{\{state\}\}/g,            salesInfo?.state || '')
    .replace(/\{\{senderFirstName\}\}/g,  senderFirst)
    .replace(/\{\{senderLastName\}\}/g,   senderLast)
    .replace(/\{\{senderFullName\}\}/g,   `${senderFirst} ${senderLast}`.trim());
};

const EMAIL_TYPE_OPTIONS = [
  { value: 'cold-outreach',       label: 'Cold Outreach (First Email)',          description: 'A professional introduction email for an agency we have never contacted before.' },
  { value: 'follow-up-no-reply',  label: 'Follow-Up (No Reply Received)',        description: 'A gentle nudge when we sent an email but heard nothing back.' },
  { value: 'post-call-follow-up', label: 'Post-Call Follow-Up',                  description: 'A recap email sent after a discovery or sales call summarizing next steps.' },
  { value: 're-engagement',       label: 'Re-Engagement (Lead Went Cold)',        description: 'Reconnects with a lead that went silent after previous contact.' },
  { value: 'ops-intro',           label: 'OPS Team Intro Email',                  description: 'Introduces the Operations team after a deal moves toward onboarding.' },
  { value: 'proposal-follow-up',  label: 'Proposal / Decision Follow-Up',        description: 'Follows up after sending a proposal or pricing, asking for a decision.' },
];

const EmailForm = ({ onSuccess, replyData, handleCloseModal, forwardData, setTab, handleFetchData, setEmailOpen, EmailList, salesInfo, draftData, onDraftSaved, onDraftDiscarded, outlookDraftData }) => {
  const location = useLocation();
  const ispersonal = location.pathname.toLowerCase().includes('/personaloutlook');
  const isSales = location.pathname.toLowerCase().includes('/salesoutlook');
  const isOutlook = ispersonal || isSales;
  const data = useSelector((state) => state.EmailData.CurrentMailId);
  const dispatch = useDispatch();
  const bodyRef = useRef(null);
  const [isReplyMode, setIsReplyMode] = useState(false);
  const email = useSelector((state) => state.EmailData.mail);
  const [formData, setFormData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    fromEmail: email || '',
  });
  const [fileList, setFileList] = useState([]);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const emailTemplates = useSelector((state) => state?.sales?.emailTemplates || []);
  const openWindow = useSelector((state) => state.EmailData.openWindow);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const [isForwardMode, setIsForwardMode] = useState(false);
  const [toError, setToError] = useState('');
  const [fromEmailError, setFromEmailError] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [includeUnsubscribe, setIncludeUnsubscribe] = useState(true);
  // Draft state
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [sending, setSending] = useState(false); // prevents duplicate sends
  const autoSaveTimerRef = useRef(null);
  // Always-current ref used by the unmount cleanup so it sees the latest state
  const latestRef = useRef({});
  // Set to true by handleClose after a successful save — prevents unmount cleanup from double-saving
  const savedOnCloseRef = useRef(false);
  // Tracks the Outlook draft message ID for create-then-update (PATCH) flow
  const outlookDraftIdRef = useRef(null);

  // --- AI Email Draft state ---
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiEmailType, setAiEmailType] = useState('cold-outreach');
  const [aiAgencyName, setAiAgencyName] = useState('');
  const aiLoading = useSelector((state) => state.aiSales.emailDraft.loading);
  const aiError = useSelector((state) => state.aiSales.emailDraft.error);

  useEffect(() => {
    if (loginUser?.id) {
      dispatch(fetchAllEmailTemplates(loginUser.id));
    }
  }, [dispatch, loginUser?.id]);

  useEffect(() => {
    if (replyData) {
      const sub = replyData.subject || '';
      setFormData({
        to: replyData.to,
        cc: replyData.cc || '',
        bcc: '',
        subject: sub.toLowerCase().startsWith('re:') ? sub : `Re: ${sub}`,
        body: '',
        fromEmail: replyData.from,
      });
      if (replyData.cc) setShowCc(true);
      setIsReplyMode(true);

      // Pre-populate editor with quoted original — Gmail/Outlook style.
      // User types above the quoted thread, sees the original below for context.
      const fromName = extractNameFromEmail(replyData.from);
      const fromAddr = replyData.from || '';
      const toName = extractNameFromEmail(replyData.to);
      const toAddr = replyData.to || '';
      const sentStr = formatDate(replyData.date);
      const quoted = `<br><br><br><hr tabindex="-1" style="display:inline-block; width:98%">
<div id="divRplyFwdMsg" dir="ltr">
<div style="margin-top: 20px; padding-top: 10px; font-family: Calibri, sans-serif; font-size: 11pt; color: #000;">
<p style="margin:0;"><b>From:</b> ${fromName} &lt;${fromAddr}&gt;</p>
<p style="margin:0;"><b>Sent:</b> ${sentStr}</p>
<p style="margin:0;"><b>To:</b> ${toName} &lt;${toAddr}&gt;</p>
<p style="margin:0;"><b>Subject:</b> ${replyData.subject || ''}</p>
<br>
<div>${replyData.body || ''}</div>
</div></div>`;
      setTimeout(() => {
        if (bodyRef.current) {
          bodyRef.current.innerHTML = quoted;
          // Place cursor at the very start so user types above the quoted thread
          try {
            const sel = window.getSelection();
            const range = document.createRange();
            range.setStart(bodyRef.current, 0);
            range.collapse(true);
            sel?.removeAllRanges();
            sel?.addRange(range);
            bodyRef.current.focus();
          } catch { /* ignore selection errors */ }
        }
      }, 0);
    }
  }, [replyData]);

  useEffect(() => {
    if (forwardData) {
      setFormData({
        to: '',
        cc: '',
        bcc: '',
        subject: forwardData.subject,
        body: forwardData.body,
        fromEmail: email || '', // use the logged-in account email, not the original sender
      });
      setIsForwardMode(true);
      setShowCc(true); // auto-show CC for forwards
    }
  }, [forwardData]);

  // Pre-load Outlook draft for editing (from Drafts folder click)
  useEffect(() => {
    if (!outlookDraftData) return;
    outlookDraftIdRef.current = outlookDraftData.id || null;
    setFormData({
      to:        outlookDraftData.to      || '',
      cc:        outlookDraftData.cc      || '',
      bcc:       outlookDraftData.bcc     || '',
      subject:   outlookDraftData.subject || '',
      body:      outlookDraftData.body    || '',
      fromEmail: outlookDraftData.from    || email || '',
    });
    if (outlookDraftData.cc)  setShowCc(true);
    if (outlookDraftData.bcc) setShowBcc(true);
    // Set body in rich text editor
    setTimeout(() => {
      if (bodyRef.current) bodyRef.current.innerHTML = outlookDraftData.body || '';
    }, 0);
  }, [outlookDraftData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-load existing draft content when editing a saved draft
  useEffect(() => {
    if (!draftData) return;
    setCurrentDraftId(draftData.id || null);
    setFormData({
      to:        draftData.emailedto  || '',
      cc:        draftData.emailedy   || '',
      bcc:       draftData.remarks    || '',
      subject:   draftData.emailBody  || '',
      body:      draftData.body       || '',
      fromEmail: draftData.emailFrom  || email || '',
    });
    if (draftData.emailedy)  setShowCc(true);
    if (draftData.remarks)   setShowBcc(true);
    // Directly inject body into the contenteditable editor
    if (draftData.body && bodyRef.current) {
      bodyRef.current.innerHTML = draftData.body;
    }
    // Restore draft attachments
    if (draftData.draftAttachments) {
      try {
        const atts = typeof draftData.draftAttachments === 'string'
          ? JSON.parse(draftData.draftAttachments)
          : draftData.draftAttachments;
        if (Array.isArray(atts) && atts.length > 0) {
          setFileList(atts.map((att, idx) => ({
            uid: `draft-att-${idx}`,
            name: att.name,
            type: att.contentType,
            size: att.size,
            status: 'done',
            s3Key: att.s3Key,
          })));
        }
      } catch { /* ignore parse errors */ }
    }
  }, [draftData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft every 60 s when form is dirty.
  // Includes replies and forwards so users don't lose work mid-compose.
  useEffect(() => {
    const hasContent = formData.to || formData.subject || formData.body;
    if (!hasContent) return;
    // Outlook mode: save to Outlook Drafts folder; lead mode: save to MongoDB
    if (isOutlook) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => performSaveOutlookDraft(false), 60000);
    } else if (salesInfo?.id) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => performSaveDraft(false), 60000);
    }

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep latestRef current on every render so the unmount cleanup reads fresh values
  latestRef.current = { formData, currentDraftId, salesInfo, isReplyMode, isForwardMode, isOutlook, outlookDraftId: outlookDraftIdRef.current };

  // Auto-save on unmount (e.g. lead tab closed while email compose is open).
  // Includes replies and forwards — losing a half-written reply on accidental
  // close is worse than having an extra draft the user can delete.
  useEffect(() => {
    return () => {
      if (savedOnCloseRef.current) return; // handleClose already saved — don't duplicate
      const { formData: fd, currentDraftId: draftId, salesInfo: si, isOutlook: outlook, outlookDraftId: odId } = latestRef.current;
      const bodyHtml = bodyRef.current?.innerHTML || fd?.body || '';
      const hasContent = fd?.to || fd?.subject || bodyHtml.replace(/<[^>]*>/g, '').trim();
      if (!hasContent) return;

      if (outlook && fd?.fromEmail) {
        // Fire-and-forget Outlook draft save on unmount
        const toList  = fd.to  ? fd.to.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
        const ccList  = fd.cc  ? fd.cc.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
        const bccList = fd.bcc ? fd.bcc.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
        fetch(`${SERVER_URL}/api/outlook/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ fromEmail: fd.fromEmail, to: toList, cc: ccList, bcc: bccList, subject: fd.subject, body: bodyHtml, draftId: odId || undefined }),
        }).catch(() => {});
      } else if (!outlook && si?.id) {
        // Fire-and-forget lead draft save on unmount
        dispatch(saveDraftThunk({
          leadId:    si.id,
          draftId:   draftId || undefined,
          emailFrom: fd.fromEmail,
          emailedto: fd.to,
          emailCC:   fd.cc,
          emailBCC:  fd.bcc,
          subject:   fd.subject,
          body:      bodyHtml,
        }));
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'to') {
      const recipients = value.split(/[,;]+/).map(email => email.trim()).filter(Boolean);
      if (recipients.length === 0) {
        setToError('This message must have at least one recipient.');
      } else if (!recipients.every(isValidEmail)) {
        setToError('One or more email addresses are invalid.');
      } else {
        setToError('');
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBodyChange = (content) => {
    setFormData(prev => ({ ...prev, body: content }));
  };

  const handleUploadChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  };

  const extractNameFromEmail = (email) => {
    if (!email) return '';
    const localPart = email.split('@')[0];
    const nameParts = localPart.includes('.')
      ? localPart.split('.')
      : localPart.match(/[A-Z][a-z]*|[a-z]+/g);
    if (!nameParts) return localPart;
    return nameParts.map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
  };

  const prepareAttachments = async () => {
    return await Promise.all(fileList.map(async file => ({
      name: file.name,
      base64: await fileToBase64(file.originFileObj),
      mimeType: file.type,
    })));
  };

  const formatDate = (isoString) => {
    const options = {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
    };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(isoString));
  };

  const validateRecipients = () => {
    if (!formData.to) return false;
    const recipients = formData.to.split(/[,;]+/).map((e) => e.trim()).filter(Boolean);
    if (recipients.length === 0) return false;
    return recipients.every(isValidEmail);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // ── Contact recipient helpers (Sales email track only, not Outlook) ──────────
  // Build options from the lead's contact list. Opted-out contacts are shown but disabled.
  const contactOptions = (!isOutlook && salesInfo?.leadsContacts)
    ? salesInfo.leadsContacts
        .filter(c => c.email?.trim())
        .map(c => ({
          value:         c.email.trim().toLowerCase(),
          label:         c.contactPerson ? `${c.contactPerson} (${c.email.trim()})` : c.email.trim(),
          contactPerson: c.contactPerson || '',
          title:         c.title || '',
          optIn:         !!c.optIn,
          disabled:      !c.optIn,
        }))
    : [];

  const hasContactOptions = contactOptions.length > 0;
  const allOptedOut       = hasContactOptions && contactOptions.every(o => o.disabled);

  // Convert a comma-separated address string ↔ array for the Select.
  const toArray  = (str) => str ? str.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
  const toString = (arr) => arr.join(', ');

  const handleToSelectChange = (vals) => {
    // Warn if a manually-typed email matches an opted-out contact
    const optedOutEmails = contactOptions.filter(o => o.disabled).map(o => o.value);
    const blocked = vals.filter(v => optedOutEmails.includes(v.toLowerCase()));
    if (blocked.length > 0) {
      message.warning(`${blocked.join(', ')} has opted out and cannot receive emails.`);
      return;
    }
    const str = toString(vals);
    setFormData(prev => ({ ...prev, to: str }));
    if (vals.length === 0) {
      setToError('This message must have at least one recipient.');
    } else if (!vals.every(isValidEmail)) {
      setToError('One or more email addresses are invalid.');
    } else {
      setToError('');
    }
  };

  const handleCcSelectChange  = (vals) => setFormData(prev => ({ ...prev, cc:  toString(vals) }));
  const handleBccSelectChange = (vals) => setFormData(prev => ({ ...prev, bcc: toString(vals) }));

  const handleGenerateDraft = async () => {
    const repName = loginUser ? `${loginUser.firstname} ${loginUser.lastname}` : '';
    const resolvedAgencyName = salesInfo?.agencyName || aiAgencyName.trim();
    if (!resolvedAgencyName) {
      message.warning('Please enter an agency name so AI can personalize the email.');
      return;
    }
    const leadContext = {
      agencyName: resolvedAgencyName,
      city: salesInfo?.city || '',
      state: salesInfo?.state || '',
      timeZone: salesInfo?.timeZone || '',
      webURL: salesInfo?.webURL || '',
      contacts: salesInfo?.leadsContacts || [],
      previousEmails: salesInfo?.leadsEmails || [],
      previousCalls: salesInfo?.leadsCalls || [],
    };
    const result = await dispatch(generateEmailDraftThunk({ leadContext, emailType: aiEmailType, repName }));
    if (generateEmailDraftThunk.fulfilled.match(result)) {
      const { subject, body } = result.payload;
      setFormData((prev) => ({ ...prev, subject }));
      if (bodyRef.current) {
        bodyRef.current.innerHTML = body.replace(/\n/g, '<br>');
      }
      handleBodyChange(body.replace(/\n/g, '<br>'));
      setAiPanelOpen(false);
      message.success('Email draft generated!');
      dispatch(clearEmailDraft());
    } else {
      message.error(result.payload || 'Failed to generate email draft');
    }
  };

  const handleSendMail = async (e) => {
    e?.preventDefault();
    if (!formData.fromEmail) {
      setFromEmailError('Please select a From Email address.');
      message.error('Please select a From Email address.');
      return;
    } else {
      setFromEmailError('');
    }
    if (!validateRecipients(formData.to)) {
      message.error('This message must have at least one valid recipient');
      return;
    }
    if (!formData.subject?.trim()) {
      Modal.confirm({
        title: 'Missing subject',
        content: 'Do you want to send this message without a subject?',
        okText: 'Send',
        cancelText: "Don't send",
        centered: true,
        onOk: () => sendNow(),
      });
      return;
    }
    await sendNow();
  };

  // Best-effort cleanup of the open Outlook draft after a successful send.
  // Without this the just-sent draft lingers in the Drafts folder forever.
  // The backend DELETE endpoint requires fromEmail in the body to identify
  // which Outlook mailbox owns the draft.
  //
  // Also sets savedOnCloseRef so the unmount-time auto-save (which fires
  // when the modal closes after send) doesn't create a brand-new draft from
  // the still-in-state form data.
  const cleanupOutlookDraftIfAny = async () => {
    // Tell the unmount handler NOT to re-create the draft after we close.
    savedOnCloseRef.current = true;
    const draftId = outlookDraftIdRef.current;
    const fromEmail = latestRef.current?.formData?.fromEmail || formData?.fromEmail;
    if (!draftId || !fromEmail) return;
    try {
      await fetch(`${SERVER_URL}/api/outlook/draft/${encodeURIComponent(draftId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ fromEmail }),
      });
    } catch { /* best-effort */ }
    outlookDraftIdRef.current = null;
  };

  const sendNow = async () => {
    if (sending) return;
    setSending(true);
    // Inline editor styles so the email renders consistently in Outlook/Gmail/etc.
    const editorHTML = inlineStylesForEmail(bodyRef.current?.innerHTML || formData.body || '');
    try {
      dispatch(setLoader(true));
      let result;
      if (ispersonal) {
        const attachments = await prepareAttachments();
        result = await dispatch(sendSharedMailboxEmail({
          fromEmail: formData.fromEmail,
          subject: formData.subject,
          body: editorHTML,
          bodyType: 'HTML',
          to: formData.to.split(/[,;]+/).map(e => e.trim()).filter(Boolean),
          cc: formData.cc?.split(/[,;]+/).map(e => e.trim()).filter(Boolean),
          bcc: formData.bcc?.split(/[,;]+/).map(e => e.trim()).filter(Boolean),
          replyTo: [],
          attachments,
        }));
        if (result.payload?.success) {
          message.success("Email sent successfully");
          if (handleFetchData) { handleFetchData(data.messageId, data.email); dispatch(setCurrentMailId({})); }
          dispatch(setOpenWindow(false));
          if (setTab) setTab(false);
          // Clean up any open Outlook draft so the sent email doesn't linger as a draft.
          cleanupOutlookDraftIfAny();
          setFormData({});
          setFileList([]);
        } else {
          message.error(result.payload?.error || "Failed to send email.");
        }
      } else {
        result = await dispatch(sendSesEmailThunk({ formData: { ...formData, sentBy: loginUser?._id || loginUser?.id, includeUnsubscribe, leadId: salesInfo?._id || salesInfo?.id }, fileList, editorHTML }));
        if (sendSesEmailThunk.fulfilled.match(result)) {
          const { totalSent, totalFailed, sendStatus } = result.payload;
          const successful = sendStatus.filter(e => e.status === 'success');
          const failed = sendStatus.filter(e => e.status === 'failed');
          if (successful.length > 0) {
            message.success("Emails sent successfully");
            const sent = successful[0];
            dispatch(prependToFolder({
              user: formData.fromEmail,
              folder: 'sentitems',
              message: {
                id: sent.messageId || `ses-${Date.now()}`,
                trackId: sent.trackId,
                subject: formData.subject || '',
                bodyPreview: (bodyRef.current?.innerText || formData.body || '').slice(0, 100),
                body: { contentType: 'HTML', content: editorHTML },
                from: { emailAddress: { name: formData.fromEmail, address: formData.fromEmail } },
                toRecipients: (formData.to || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
                ccRecipients: (formData.cc || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
                bccRecipients: (formData.bcc || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
                sentDateTime: sent.sentDateTime || new Date().toISOString(),
                isRead: true,
                conversationId: sent.messageId || `ses-${Date.now()}`,
                provider: 'ses',
              },
            }));
            if (onSuccess) {
              onSuccess({ ...formData, sendStatus, totalSent, totalFailed });
            }
            // If this was an open draft, remove it from the lead record after successful send
            if (currentDraftId && salesInfo?.id) {
              dispatch(discardDraftThunk({ leadId: salesInfo.id, draftId: currentDraftId }))
                .unwrap()
                .then(result => { if (onDraftDiscarded) onDraftDiscarded(currentDraftId, result?.leadsEmails || []); })
                .catch(() => {}); // best-effort
            }
            // Also clean up any open Outlook draft so it doesn't linger in Drafts.
            cleanupOutlookDraftIfAny();
            setCurrentDraftId(null);
            setFormData({});
            setFileList([]);
            dispatch(setOpenWindow(false));
            if (setTab) setTab(false);
          }
          if (failed.length > 0) {
            message.error(`Some emails failed:\n${failed.map(e => `${e.email} - ${e.error}`).join('\n')}`);
          }
        } else if (sendSesEmailThunk.rejected.match(result)) {
          message.error(result.payload || "Failed to send emails.");
        }
      }
    } catch (err) {
      message.error("Unexpected error occurred.");
    } finally {
      setSending(false);
      dispatch(setLoader(false));
    }
  };

  const handleReplyMail = async (e) => {
    e?.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      dispatch(setLoader(true));
      // Editor already contains user's reply + the pre-populated quoted thread.
      // Inline styles on the whole thing so lists/formatting render in email clients.
      const fullMessage = inlineStylesForEmail(bodyRef.current?.innerHTML || formData.body || '');

      // When NOT on Outlook path (leads email tracker), send via SES so the reply
      // gets a SesTracking record with emailType:"reply" and appears in Email History
      // with correct lead linkage and in the lead's EmailTrack.
      if (!isOutlook) {
        const leadId = salesInfo?._id || salesInfo?.id;
        const result = await dispatch(sendSesEmailThunk({
          formData: {
            fromEmail: formData.fromEmail,
            to: formData.to,
            cc: formData.cc || '',
            bcc: formData.bcc || '',
            subject: formData.subject,
            emailType: 'reply',
            parentMessageId: replyData?.messageId,
            inReplyTo: replyData?.messageId,
            sentBy: loginUser?._id || loginUser?.id,
            includeUnsubscribe,
            ...(leadId && { leadId }),
          },
          fileList,
          editorHTML: fullMessage,
        }));
        if (sendSesEmailThunk.fulfilled.match(result)) {
          // Immediately add to Sent Items so the user sees it without waiting for the 60s poll
          const sent = result.payload?.sendStatus?.[0];
          if (sent?.status === 'success') {
            dispatch(prependToFolder({
              user: formData.fromEmail,
              folder: 'sentitems',
              message: {
                id: sent.messageId || `ses-reply-${Date.now()}`,
                trackId: sent.trackId,
                messageId: sent.messageId,
                subject: formData.subject || '',
                bodyPreview: (bodyRef.current?.innerText || formData.body || '').slice(0, 100),
                body: { contentType: 'HTML', content: fullMessage },
                from: { emailAddress: { name: formData.fromEmail, address: formData.fromEmail } },
                toRecipients: (formData.to || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
                ccRecipients: (formData.cc || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
                bccRecipients: (formData.bcc || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
                sentDateTime: sent.sentDateTime || new Date().toISOString(),
                isRead: true,
                // Use parent's messageId as conversationId so dedup collapses this with the original
                conversationId: replyData?.messageId || sent.messageId || `ses-reply-${Date.now()}`,
                provider: 'ses',
                emailType: 'reply',
              },
            }));
          }
          if (onSuccess) await onSuccess(result.payload);
          if (handleFetchData && data?.messageId && data?.email) {
            handleFetchData(data.messageId, data.email);
            dispatch(setCurrentMailId({}));
          }
          // Clean up any open Outlook draft after successful reply.
          cleanupOutlookDraftIfAny();
          if (bodyRef.current) bodyRef.current.innerHTML = '';
          setFormData({});
          handleCloseModal?.();
          message.success('Reply sent successfully');
          setFileList([]);
        } else {
          message.error(result.payload || 'Failed to send reply.');
        }
        return;
      }

      // Outlook path — use Graph API replyToMailThunk (unchanged)
      const attachments = await Promise.all(fileList.map(async file => ({
        name: file.name,
        type: file.type,
        content: await fileToBase64(file.originFileObj),
      })));
      const replyResult = await dispatch(replyToMailThunk({
        messageId: replyData.messageId,
        replyContent: fullMessage,
        attachments,
        subject: formData.subject,
        from: formData.fromEmail,
        to: formData.to,
        cc: formData.cc || '',
        bcc: formData.bcc || '',
      }));
      // Optimistically add the reply to sent items with its trackId so TrackingPanel works
      if (replyResult?.trackId) {
        dispatch(prependToFolder({
          user: formData.fromEmail,
          folder: 'sentitems',
          message: {
            id: replyResult.graphMessageId || `reply-${Date.now()}`,
            trackId: replyResult.trackId,
            subject: formData.subject || '',
            bodyPreview: (bodyRef.current?.innerText || formData.body || '').slice(0, 100),
            body: { contentType: 'HTML', content: fullMessage },
            from: { emailAddress: { name: formData.fromEmail, address: formData.fromEmail } },
            toRecipients: (formData.to || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
            ccRecipients: (formData.cc || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
            bccRecipients: (formData.bcc || '').split(/[,;]+/).map(a => a.trim()).filter(Boolean).map(a => ({ emailAddress: { address: a } })),
            sentDateTime: new Date().toISOString(),
            isRead: true,
            conversationId: replyData.messageId,
            provider: 'graph-reply',
          },
        }));
      }
      if (data?.messageId && data?.email) {
        if (handleFetchData) { handleFetchData(data.messageId, data.email); }
        dispatch(setCurrentMailId({}));
      }
      // Clean up any open Outlook draft after successful Outlook-path reply.
      cleanupOutlookDraftIfAny();
      if (bodyRef.current) bodyRef.current.innerHTML = '';
      setFormData({});
      handleCloseModal?.();
      message.success('Reply sent successfully');
      setFileList([]);
    } catch (error) {
      console.error('Reply error details:', error);
      const errMsg = typeof error === 'string' ? error
        : error?.error || error?.message || 'Server error while sending reply.';
      message.error(typeof errMsg === 'string' ? errMsg : 'Server error while sending reply.');
    } finally {
      setSending(false);
      dispatch(setLoader(false));
    }
  };

  const handleTemplateSelect = (template) => {
    // If a To contact is already selected, use them as the primary for variable resolution
    // so {{contactFirstName}} reflects the actual recipient, not just leadsContacts[0].
    let effectiveSalesInfo = salesInfo;
    const selectedEmails = toArray(formData.to);
    if (selectedEmails.length > 0 && salesInfo?.leadsContacts?.length > 0) {
      const matched = salesInfo.leadsContacts.find(c =>
        c.email && selectedEmails.some(e => e.toLowerCase() === c.email.toLowerCase())
      );
      if (matched) {
        effectiveSalesInfo = {
          ...salesInfo,
          leadsContacts: [matched, ...salesInfo.leadsContacts.filter(c => c !== matched)],
        };
      }
    }

    const resolvedSubject = applyTemplateVariables(template.subject || '', effectiveSalesInfo, loginUser);
    const resolvedBody    = applyTemplateVariables(template.body    || '', effectiveSalesInfo, loginUser);
    setFormData(prev => ({ ...prev, subject: resolvedSubject || prev.subject }));
    if (bodyRef.current) {
      bodyRef.current.innerHTML = resolvedBody;
    }
    handleBodyChange(resolvedBody);
    setTemplatePickerOpen(false);
    message.success(`Template "${template.name}" applied`);
  };

  const handleForwardMail = async (e) => {
    e?.preventDefault();
    if (sending) return;
    if (!formData.fromEmail) {
      message.error('Please select a From Email address.');
      return;
    }
    if (!validateRecipients(formData.to)) {
      message.error('This message must have at least one valid recipient');
      return;
    }
    setSending(true);
    try {
      dispatch(setLoader(true));
      // Inline editor styles so the forward renders consistently in Outlook/Gmail/etc.
      const editorHTML = inlineStylesForEmail(bodyRef.current?.innerHTML || formData.body || '');
      const attachments = await Promise.all(fileList.map(async file => ({
        name: file.name,
        type: file.type,
        content: await fileToBase64(file.originFileObj),
      })));
      const payload = {
        messageId: forwardData.messageId,
        from: formData.fromEmail,
        to: formData.to,
        cc: formData.cc,
        bcc: formData.bcc,
        comment: editorHTML,
        attachments,
      };
      await dispatch(forwardEmailThunk(payload)).unwrap();
      // Clean up any open Outlook draft after successful forward.
      cleanupOutlookDraftIfAny();
      handleCloseModal();
      message.success('Email forwarded successfully');
      setFormData({});
      setFileList([]);
    } catch (error) {
      console.error(error);
      message.error('Error forwarding email');
    } finally {
      setSending(false);
      dispatch(setLoader(false));
    }
  };

  const handleOpenInNewWindow = () => {
    dispatch(setOpenWindow(true));
    if (setTab) setTab(false);
  };

  // Core draft-save logic — called by button, auto-save, and close prompt
  const performSaveDraft = async (showToast = true) => {
    const bodyHtml = bodyRef.current?.innerHTML || formData.body || '';
    // If we have a lead context, save to backend
    if (salesInfo?.id) {
      setAutoSaveStatus('saving');
      try {
        // Upload new attachments to S3; reuse already-uploaded ones
        const uploadedAttachments = await Promise.all(
          (fileList || []).map(async (file) => {
            if (file.s3Key) return { name: file.name, contentType: file.type || file.contentType, size: file.size, s3Key: file.s3Key };
            if (!file.originFileObj) return null;
            try {
              const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(file.originFileObj);
              });
              const res = await axios.post(`${SERVER_URL}/api/ses/upload-draft-attachment`, {
                filename: file.name, contentType: file.type, base64,
              }, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` } });
              // Update the fileList entry with s3Key so re-save doesn't re-upload
              file.s3Key = res.data.s3Key;
              return res.data;
            } catch { return null; }
          })
        ).then(results => results.filter(Boolean));

        const result = await dispatch(saveDraftThunk({
          leadId:    salesInfo.id,
          draftId:   currentDraftId || undefined,
          emailFrom: formData.fromEmail,
          emailedto: formData.to,
          emailCC:   formData.cc,
          emailBCC:  formData.bcc,
          subject:   formData.subject,
          body:      bodyHtml,
          draftAttachments: JSON.stringify(uploadedAttachments),
        })).unwrap();
        // Capture the subdocument _id on first save so subsequent saves update rather than create
        if (!currentDraftId && result?.leadsEmails) {
          const newest = result.leadsEmails.find(e => e.isDraft);
          if (newest?.id) setCurrentDraftId(newest.id);
        }
        if (onDraftSaved) onDraftSaved(result?.leadsEmails || []);
        setAutoSaveStatus('saved');
        if (showToast) message.success('Draft saved');
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
        return true;
      } catch (err) {
        setAutoSaveStatus('error');
        if (showToast) message.error('Failed to save draft');
        return false;
      }
    }
    // Fallback: no lead context (Outlook compose) — keep localStorage
    const draft = { key: `draft_${Date.now()}`, ...formData, body: bodyHtml, savedAt: new Date().toISOString() };
    const drafts = JSON.parse(localStorage.getItem('email_drafts') || '[]');
    drafts.unshift(draft);
    localStorage.setItem('email_drafts', JSON.stringify(drafts.slice(0, 20)));
    if (showToast) message.success('Draft saved');
    return true;
  };

  // Save draft to Outlook Drafts folder via Graph API (creates once, then PATCHes on subsequent saves)
  const performSaveOutlookDraft = async (showToast = false) => {
    const bodyHtml = bodyRef.current?.innerHTML || formData.body || '';
    const hasContent = formData.to || formData.subject || bodyHtml.replace(/<[^>]*>/g, '').trim();
    if (!hasContent || !formData.fromEmail) return null;
    setAutoSaveStatus('saving');
    try {
      const toList  = formData.to  ? formData.to.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
      const ccList  = formData.cc  ? formData.cc.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
      const bccList = formData.bcc ? formData.bcc.split(/[,;]+/).map(e => e.trim()).filter(Boolean) : [];
      const res = await fetch(`${SERVER_URL}/api/outlook/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          fromEmail: formData.fromEmail,
          to: toList, cc: ccList, bcc: bccList,
          subject: formData.subject,
          body: bodyHtml,
          draftId: outlookDraftIdRef.current || undefined,
        }),
      });
      const data = await res.json();
      if (data?.draftId) outlookDraftIdRef.current = data.draftId;
      setAutoSaveStatus('saved');
      if (showToast) message.success('Draft saved to Outlook');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
      return true;
    } catch (err) {
      console.error('Outlook draft save failed:', err);
      setAutoSaveStatus('error');
      return null;
    }
  };

  // Called when user explicitly clicks "Save as Draft"
  const saveDraft = () => isOutlook ? performSaveOutlookDraft(true) : performSaveDraft(true);

  // Called when user explicitly chooses "Discard" from the dropdown
  const doDiscard = async () => {
    if (currentDraftId && salesInfo?.id) {
      try {
        const result = await dispatch(discardDraftThunk({ leadId: salesInfo.id, draftId: currentDraftId })).unwrap();
        if (onDraftDiscarded) onDraftDiscarded(currentDraftId, result?.leadsEmails || []);
      } catch { /* discard best-effort */ }
    }
    setFormData({ to: '', cc: '', bcc: '', subject: '', body: '', fromEmail: email || '' });
    if (bodyRef.current) bodyRef.current.innerHTML = '';
    setCurrentDraftId(null);
  };

  // Called when X/close is clicked — auto-saves draft silently then closes.
  // Includes replies and forwards so half-written replies aren't lost.
  const handleClose = async () => {
    const bodyHtml = bodyRef.current?.innerHTML || '';
    const hasContent = formData.to || formData.subject || bodyHtml.replace(/<[^>]*>/g, '').trim();
    if (hasContent) {
      if (salesInfo?.id) {
        // Lead context always wins — save to MongoDB regardless of Outlook mode
        const saved = await performSaveDraft(false);
        if (saved) savedOnCloseRef.current = true;
      } else if (isOutlook) {
        await performSaveOutlookDraft(false);
        savedOnCloseRef.current = true;
      }
    }
    // For reply/forward modal that has a handleCloseModal prop, close that.
    // Otherwise close the parent tab/window like before.
    if (isReplyMode || isForwardMode) { handleCloseModal?.(); return; }
    if (setTab) { setTab(false); if (openWindow) dispatch(setOpenWindow(false)); }
    else if (setEmailOpen) setEmailOpen(false);
  };

  const sendDropdownItems = [
    {
      key: 'schedule',
      label: <span><FontAwesomeIcon icon={faClock} style={{ marginRight: 8 }} />Schedule Send</span>,
    },
    {
      key: 'draft',
      label: <span><FontAwesomeIcon icon={faFloppyDisk} style={{ marginRight: 8 }} />Save as Draft</span>,
    },
    { type: 'divider' },
    {
      key: 'discard',
      danger: true,
      label: <span><FontAwesomeIcon icon={faTrash} style={{ marginRight: 8 }} />Discard</span>,
    },
  ];

  const handleSendDropdown = ({ key }) => {
    if (key === 'schedule') setScheduleModalOpen(true);
    else if (key === 'draft') saveDraft();
    else if (key === 'discard') doDiscard();
  };

  const primaryAction = isReplyMode ? handleReplyMail : isForwardMode ? handleForwardMail : handleSendMail;
  const sendLabel = isReplyMode ? 'Send Reply' : isForwardMode ? 'Forward' : 'Send';

  return (
    <div style={{ maxWidth: '100%' }}>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #e5e7eb' }}>

        {/* Split Send button */}
        <Button.Group>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 6 }} />}
            onClick={primaryAction}
            loading={sending}
            disabled={sending}
            style={{ borderRadius: '6px 0 0 6px' }}
          >
            {sendLabel}
          </Button>
          <Dropdown
            menu={{ items: sendDropdownItems, onClick: handleSendDropdown }}
            placement="bottomRight"
          >
            <Button
              type="primary"
              style={{ borderRadius: '0 6px 6px 0', padding: '0 8px', borderLeft: '1px solid rgba(255,255,255,0.3)' }}
            >
              <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11 }} />
            </Button>
          </Dropdown>
        </Button.Group>

        {/* Templates picker — new compose only */}
        {!isReplyMode && !isForwardMode && (
          <AntdButton
            type="default"
            icon={<FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 4 }} />}
            label="Templates"
            onClick={() => setTemplatePickerOpen(true)}
          />
        )}

        {/* AI Draft — new compose only */}
        {!isReplyMode && !isForwardMode && (
          <AntdButton
            style={{
              background: aiPanelOpen ? '#7c3aed' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
            }}
            icon={<FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 4 }} />}
            label={
              <span>
                AI Draft&nbsp;
                <FontAwesomeIcon icon={aiPanelOpen ? faChevronUp : faChevronDown} style={{ fontSize: 11 }} />
              </span>
            }
            onClick={() => setAiPanelOpen((v) => !v)}
          />
        )}

        {/* Unsubscribe link toggle */}
        {!isOutlook && (
          <Tooltip title="Include an unsubscribe link at the bottom of the email">
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: includeUnsubscribe ? '#10b981' : '#9ca3af', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={includeUnsubscribe}
                onChange={(e) => setIncludeUnsubscribe(e.target.checked)}
                style={{ accentColor: '#10b981', cursor: 'pointer' }}
              />
              Unsubscribe Link
            </label>
          </Tooltip>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {/* Expand to new window */}
          {!openWindow && setTab && !isReplyMode && !isForwardMode && (
            <Tooltip title="Open in new window">
              <span>
                <AntdButton icon={<FontAwesomeIcon icon={faUpRightFromSquare} />} onClick={handleOpenInNewWindow} />
              </span>
            </Tooltip>
          )}
          {/* Auto-save indicator */}
          {autoSaveStatus === 'saving' && (
            <span style={{ fontSize: 11, color: '#6b7280' }}><Spin size="small" />&nbsp;Saving…</span>
          )}
          {autoSaveStatus === 'saved' && (
            <span style={{ fontSize: 11, color: '#10b981' }}>✓ Saved</span>
          )}
          {/* Close */}
          <Tooltip title="Close">
            <span>
              <AntdButton
                style={{ border: 'none', backgroundColor: 'transparent', boxShadow: 'none' }}
                icon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16, color: '#6b7280' }} />}
                onClick={handleClose}
              />
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── AI Draft Panel ────────────────────────────────────────────────── */}
      {aiPanelOpen && (
        <div style={{
          background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
          border: '1px solid #c4b5fd',
          borderRadius: 8,
          padding: '14px 16px',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: '#7c3aed' }} />
            <span style={{ fontWeight: 600, color: '#5b21b6', fontSize: 14 }}>
              AI Email Draft — {salesInfo?.agencyName || 'Lead'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {!salesInfo?.agencyName && (
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Agency Name</label>
                <input
                  placeholder="Enter agency name"
                  value={aiAgencyName}
                  onChange={(e) => setAiAgencyName(e.target.value)}
                  style={{ width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }}
                />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 220 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Email Type</label>
              <Select
                value={aiEmailType}
                onChange={(val) => setAiEmailType(val)}
                style={{ width: '100%' }}
                options={EMAIL_TYPE_OPTIONS}
                optionRender={(option) => (
                  <Tooltip title={option.data.description} placement="right" mouseEnterDelay={0.3}>
                    <span style={{ display: 'block', width: '100%' }}>{option.label}</span>
                  </Tooltip>
                )}
              />
            </div>
            <AntdButton
              style={{ marginTop: 18, background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, minWidth: 140 }}
              disabled={aiLoading}
              onClick={handleGenerateDraft}
              label={
                aiLoading
                  ? <span><Spin size="small" style={{ marginRight: 6 }} />Generating...</span>
                  : <span><FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 6 }} />Generate Draft</span>
              }
            />
          </div>
          {aiError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{aiError}</div>}
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
            The AI will write a subject line and body based on the lead's details and interaction history. You can edit the draft before sending.
          </div>
        </div>
      )}

      {/* ── Form Fields ──────────────────────────────────────────────────── */}

      {/* From */}
      <SelectInput
        placeholder="Select From Email(s)"
        value={formData.fromEmail || undefined}
        name="fromEmail"
        disabled={!!replyData}
        onChange={(value) => {
          handleChange({ target: { name: 'fromEmail', value } });
          setFromEmailError('');
        }}
        style={{ width: '100%', marginBottom: 8 }}
        options={EmailList?.map((emailObj) => ({ value: emailObj.value, label: emailObj.value }))}
        error={fromEmailError}
      />

      {/* Opted-out warning — shown when all lead contacts have opted out */}
      {!isOutlook && allOptedOut && (
        <div style={{
          background: '#fef9c3', border: '1px solid #fbbf24', borderRadius: 6,
          padding: '6px 12px', marginBottom: 8, fontSize: 12, color: '#92400e',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          ⚠️ All contacts for this lead have opted out. You can still type email addresses manually.
        </div>
      )}

      {/* To + Cc/Bcc toggle buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          {!isOutlook && hasContactOptions && !allOptedOut ? (
            <>
              <Select
                mode="tags"
                placeholder="Select or type recipient emails…"
                value={toArray(formData.to)}
                onChange={handleToSelectChange}
                options={contactOptions}
                filterOption={(input, opt) => {
                  const q = input.toLowerCase();
                  return (opt.value || '').toLowerCase().includes(q) ||
                         (opt.contactPerson || '').toLowerCase().includes(q);
                }}
                optionRender={(opt) => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: opt.data.disabled ? '#94a3b8' : '#1e293b' }}>
                        {opt.data.contactPerson || opt.data.value}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{opt.data.value}</div>
                      {opt.data.title && <div style={{ fontSize: 10, color: '#b0bec5' }}>{opt.data.title}</div>}
                    </div>
                    <Tag
                      color={opt.data.optIn ? 'green' : 'red'}
                      style={{ fontSize: 10, borderRadius: 20, margin: 0, flexShrink: 0 }}
                    >
                      {opt.data.optIn ? 'Opt-in' : 'Opted out'}
                    </Tag>
                  </div>
                )}
                tagRender={({ value, onClose }) => {
                  const contact = contactOptions.find(c => c.value === value?.toLowerCase());
                  const display = contact?.contactPerson ? contact.contactPerson.split(' ')[0] : value;
                  const isOptedOut = contact && !contact.optIn;
                  return (
                    <Tag
                      closable
                      onClose={onClose}
                      style={{
                        marginRight: 4, borderRadius: 12,
                        background: isOptedOut ? '#fee2e2' : '#e0f2fe',
                        borderColor: isOptedOut ? '#fca5a5' : '#7dd3fc',
                        color: isOptedOut ? '#b91c1c' : '#0369a1',
                      }}
                    >
                      {display}
                    </Tag>
                  );
                }}
                style={{ width: '100%' }}
                notFoundContent={<span style={{ fontSize: 12, color: '#94a3b8', padding: 8 }}>Type an email address</span>}
              />
              {toError && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 3 }}>{toError}</div>}
            </>
          ) : (
            <InputField
              placeholder="To"
              name="to"
              value={formData.to}
              onChange={handleChange}
              error={toError}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, paddingTop: 6 }}>
          <Button
            size="small"
            type={showCc ? 'primary' : 'default'}
            onClick={() => setShowCc((v) => !v)}
          >
            Cc
          </Button>
          <Button
            size="small"
            type={showBcc ? 'primary' : 'default'}
            onClick={() => setShowBcc((v) => !v)}
          >
            Bcc
          </Button>
        </div>
      </div>

      {showCc && (
        !isOutlook && hasContactOptions ? (
          <Select
            mode="tags"
            placeholder="Cc — select or type emails…"
            value={toArray(formData.cc)}
            onChange={handleCcSelectChange}
            options={contactOptions}
            filterOption={(input, opt) => {
              const q = input.toLowerCase();
              return (opt.value || '').toLowerCase().includes(q) ||
                     (opt.contactPerson || '').toLowerCase().includes(q);
            }}
            optionRender={(opt) => (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{opt.data.contactPerson || opt.data.value}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{opt.data.value}</div>
                </div>
                <Tag color={opt.data.optIn ? 'green' : 'red'} style={{ fontSize: 10, borderRadius: 20, margin: 0 }}>
                  {opt.data.optIn ? 'Opt-in' : 'Opted out'}
                </Tag>
              </div>
            )}
            tagRender={({ value, closable, onClose }) => {
              const contact = contactOptions.find(c => c.value === value?.toLowerCase());
              const display = contact?.contactPerson ? contact.contactPerson.split(' ')[0] : value;
              return (
                <Tag closable={closable} onClose={onClose}
                  style={{ marginRight: 4, borderRadius: 12, background: '#f0fdf4', borderColor: '#86efac', color: '#166534' }}>
                  {display}
                </Tag>
              );
            }}
            style={{ width: '100%', marginBottom: 8 }}
            notFoundContent={<span style={{ fontSize: 12, color: '#94a3b8', padding: 8 }}>Type an email address</span>}
          />
        ) : (
          <InputField
            placeholder="Cc"
            name="cc"
            value={formData.cc}
            onChange={handleChange}
            style={{ marginBottom: 8 }}
          />
        )
      )}
      {showBcc && (
        !isOutlook && hasContactOptions ? (
          <Select
            mode="tags"
            placeholder="Bcc — select or type emails…"
            value={toArray(formData.bcc)}
            onChange={handleBccSelectChange}
            options={contactOptions}
            filterOption={(input, opt) => {
              const q = input.toLowerCase();
              return (opt.value || '').toLowerCase().includes(q) ||
                     (opt.contactPerson || '').toLowerCase().includes(q);
            }}
            optionRender={(opt) => (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{opt.data.contactPerson || opt.data.value}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{opt.data.value}</div>
                </div>
                <Tag color={opt.data.optIn ? 'green' : 'red'} style={{ fontSize: 10, borderRadius: 20, margin: 0 }}>
                  {opt.data.optIn ? 'Opt-in' : 'Opted out'}
                </Tag>
              </div>
            )}
            tagRender={({ value, closable, onClose }) => {
              const contact = contactOptions.find(c => c.value === value?.toLowerCase());
              const display = contact?.contactPerson ? contact.contactPerson.split(' ')[0] : value;
              return (
                <Tag closable={closable} onClose={onClose}
                  style={{ marginRight: 4, borderRadius: 12, background: '#faf5ff', borderColor: '#d8b4fe', color: '#6b21a8' }}>
                  {display}
                </Tag>
              );
            }}
            style={{ width: '100%' }}
            notFoundContent={<span style={{ fontSize: 12, color: '#94a3b8', padding: 8 }}>Type an email address</span>}
          />
        ) : (
          <InputField
            placeholder="Bcc"
            name="bcc"
            value={formData.bcc}
            onChange={handleChange}
            style={{ marginBottom: 8 }}
          />
        )
      )}

      {/* Subject — always visible; disabled/readonly in reply mode */}
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <span style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 13, fontWeight: 600, color: '#64748b', pointerEvents: 'none', zIndex: 1,
        }}>
          Subject:
        </span>
        <InputField
          placeholder="Enter email subject line…"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          disabled={isReplyMode}
          style={{ paddingLeft: 72, background: isReplyMode ? '#f9fafb' : undefined }}
        />
      </div>

      {/* Body */}
      <RichTextEditor ref={bodyRef} onChange={handleBodyChange} defaultValue={draftData?.body || ''} />

      {/* Attachments */}
      <Upload multiple fileList={fileList} beforeUpload={() => false} onChange={handleUploadChange}>
        <AntdButton style={{ marginTop: 12 }} type="default" icon={<FontAwesomeIcon icon={faPaperclip} />} label="Attach Files" />
      </Upload>

      {/* ── Template Picker Modal ────────────────────────────────────────── */}
      <Modal
        open={templatePickerOpen}
        onCancel={() => setTemplatePickerOpen(false)}
        footer={null}
        width={560}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faFileAlt} style={{ color: '#6366f1' }} />
            <span style={{ fontWeight: 700 }}>Choose a Template</span>
          </div>
        }
      >
        {emailTemplates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
            <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 28, marginBottom: 10, display: 'block', margin: '0 auto 10px' }} />
            No templates yet. Create them from <strong>Sales → Email Templates</strong>.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
            {emailTemplates.map(t => {
              const varCount = new Set([
                ...((t.subject || '').match(/\{\{\w+\}\}/g) || []),
                ...((t.body    || '').match(/\{\{\w+\}\}/g) || []),
              ]).size;
              return (
                <div
                  key={t.id}
                  onClick={() => handleTemplateSelect(t)}
                  style={{
                    padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                    cursor: 'pointer', transition: 'all 0.15s', background: '#fff',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f0f0ff'; e.currentTarget.style.borderColor = '#a5b4fc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', flex: 1 }}>{t.name}</span>
                    {varCount > 0 && (
                      <span style={{ fontSize: 11, color: '#7c3aed', background: '#ede9fe', borderRadius: 20, padding: '1px 8px', whiteSpace: 'nowrap' }}>
                        {varCount} variable{varCount > 1 ? 's' : ''} auto-filled
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>
                    <span style={{ color: '#94a3b8' }}>Subject: </span>{t.subject}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* ── Schedule Send Modal ──────────────────────────────────────────── */}
      <Modal
        title="Schedule Send"
        open={scheduleModalOpen}
        onCancel={() => setScheduleModalOpen(false)}
        onOk={async () => {
          if (!scheduleDate) {
            message.warning('Please select a date and time.');
            return;
          }
          if (!formData.fromEmail) {
            message.error('Please select a From email address first.');
            return;
          }
          if (!formData.to) {
            message.error('Please add at least one recipient.');
            return;
          }
          try {
            dispatch(setLoader(true));
            // Inline editor styles so scheduled email renders consistently in Outlook/Gmail/etc.
            const editorHTML = inlineStylesForEmail(bodyRef.current?.innerHTML || formData.body || '');
            const attachments = await prepareAttachments();
            await axios.post(`${SERVER_URL}/api/ses/schedule`, {
              fromEmail: formData.fromEmail,
              to: formData.to,
              cc: formData.cc || '',
              bcc: formData.bcc || '',
              subject: formData.subject || '',
              body: editorHTML,
              attachments,
              scheduledAt: scheduleDate.toISOString(),
            });
            message.success(`Email scheduled for ${scheduleDate.format('MMM D, YYYY h:mm A')}`);
            // Clean up any open Outlook draft after successful schedule.
            cleanupOutlookDraftIfAny();
            setScheduleModalOpen(false);
            setScheduleDate(null);
            setFormData({ to: '', cc: '', bcc: '', subject: '', body: '', fromEmail: email || '' });
            if (bodyRef.current) bodyRef.current.innerHTML = '';
            if (setTab) setTab(false);
          } catch (err) {
            message.error(err?.response?.data?.error || 'Failed to schedule email.');
          } finally {
            dispatch(setLoader(false));
          }
        }}
        okText="Schedule"
        centered
      >
        <p style={{ marginBottom: 12, color: '#6b7280', fontSize: 13 }}>Select when to send this email:</p>
        <DatePicker
          showTime={{ format: 'HH:mm' }}
          format="YYYY-MM-DD HH:mm"
          value={scheduleDate}
          onChange={setScheduleDate}
          style={{ width: '100%' }}
          disabledDate={(current) => current && current.valueOf() < Date.now()}
        />
      </Modal>

    </div>
  );
};

export default React.memo(EmailForm);
